/**
 * R8's compact `Your pools` list: the pool mark, the pool name, the fan's
 * state pill, and a `See all` action into `/pools`. `statusLabel` is the
 * server's written string and is rendered verbatim per decision C18; this
 * component never parses, translates, or re-cases it.
 *
 * A selectable row links to /pools?poolId=; a pending, suspended or archived
 * one stays inert, matching PoolCard (ISSUE-1028).
 */
import { cn, FOCUS_RING, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { typePoolCardModel } from "../pools/poolsListState";
import { PoolCardShare } from "../pools/PoolCardShare";
import { PoolCardBody } from "../pools/PoolCard";
import {
  ACCOUNT_POOLS_PREVIEW_LIMIT,
  GO_TO_POOLS_ACTION,
  POOLS_EMPTY,
  POOLS_UNAVAILABLE,
  SEE_ALL_ACTION,
  YOUR_POOLS_TITLE,
} from "./accountHomeCopy";

export type typeAccountPoolsListProps = {
  isLoading: boolean;
  hasFailed: boolean;
  cards: readonly typePoolCardModel[];
  testId?: string | undefined;
};

export const AccountPoolsList = (
  props: typeAccountPoolsListProps,
): ReactNode => (
  <section
    className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4"
    data-testid={props.testId}
  >
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-h2 text-ink">{YOUR_POOLS_TITLE}</h2>
      <Link
        to="/pools"
        data-testid="account-pools-see-all"
        className="inline-flex min-h-touch-target items-center text-body-strong text-primary underline-offset-2 hover:underline"
      >
        {SEE_ALL_ACTION}
      </Link>
    </div>

    {props.isLoading ? (
      <div className="flex flex-col gap-2" data-testid="account-pools-loading">
        <Skeleton shape="text" />
        <Skeleton shape="text" />
        <Skeleton shape="text" />
      </div>
    ) : props.hasFailed ? (
      <p
        className="text-body text-ink-secondary"
        data-testid="account-pools-unavailable"
      >
        {POOLS_UNAVAILABLE}
      </p>
    ) : props.cards.length === 0 ? (
      <div className="flex flex-col items-start gap-1">
        <p
          className="text-body text-ink-secondary"
          data-testid="account-pools-empty"
        >
          {POOLS_EMPTY}
        </p>
        <Link
          to="/pools"
          data-testid="account-pools-go"
          className="inline-flex min-h-touch-target items-center text-body-strong text-primary underline-offset-2 hover:underline"
        >
          {GO_TO_POOLS_ACTION}
        </Link>
      </div>
    ) : (
      <ul
        className="flex flex-col divide-y divide-line"
        data-testid="account-pools"
      >
        {props.cards.slice(0, ACCOUNT_POOLS_PREVIEW_LIMIT).map((card) => (
          <li key={card.poolId} data-testid={`account-pool-${card.poolId}`}>
            {card.isSelectable ? (
              <Link
                to="/pools"
                search={{ poolId: card.poolId }}
                data-testid={`account-pool-link-${card.poolId}`}
                className={cn(
                  "flex min-h-touch-target items-center gap-3 rounded-md py-2",
                  FOCUS_RING,
                )}
              >
                <PoolCardBody model={card} />
              </Link>
            ) : (
              <div className="flex items-center gap-3 py-2">
                <PoolCardBody model={card} />
              </div>
            )}
            {card.isSelectable && card.adminRole ? (
              <div className="pool-card-share flex justify-end">
                <PoolCardShare poolId={card.poolId} poolName={card.name} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    )}
  </section>
);
